import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./msal";
import { config } from "../config";
import { IconShield } from "../ui/icons";

/**
 * Cloudflare Turnstile anti-bot gate. Renders the widget and reports the passing token — same
 * mechanism as the sales-portal checkout. Returns null token until the challenge is solved.
 */
function useTurnstile() {
  const ref = useRef<HTMLDivElement>(null);
  const [token, setToken] = useState<string | null>(null);
  const [failOpen, setFailOpen] = useState(false);
  useEffect(() => {
    let done = false;
    const id = setInterval(() => {
      const ts = (window as unknown as { turnstile?: { render: (el: HTMLElement, o: unknown) => void } }).turnstile;
      if (ts && ref.current && !done) {
        done = true;
        clearInterval(id);
        ts.render(ref.current, {
          sitekey: config.turnstileSiteKey,
          theme: "auto",
          callback: (t: string) => setToken(t),
          "expired-callback": () => setToken(null),
          "error-callback": () => setToken(null),
        });
      }
    }, 200);
    // Fail-open safety: if Turnstile can't load/verify (CDN blocked, hostname edge case), never lock
    // the OWNER out of their own console — MSAL owner-gate is the real access control, so a bot that
    // slips past here still can't sign in.
    const failTimer = setTimeout(() => setFailOpen(true), 12000);
    return () => { clearInterval(id); clearTimeout(failTimer); };
  }, []);
  return { ref, ready: token !== null || failOpen, verified: token !== null };
}

/** P1 — login screen. Owner-only is enforced by the backend; this just starts sign-in. */
export function Login() {
  const { instance } = useMsal();
  const { ref, ready, verified } = useTurnstile();
  return (
    <div className="login-screen">
      <div className="card login-card fade-up">
        <div style={{ background: "#fff", borderRadius: 14, padding: "16px 12px", marginBottom: 18 }}>
          <img src="/arvetio-logo-vertical.svg" alt="Arvetio — Elevate Your Microsoft 365" style={{ height: 104, display: "block", margin: "0 auto" }} />
        </div>
        <p className="muted" style={{ marginTop: 0, marginBottom: 20, fontSize: 13 }}>
          Konsola licencji — panel właściciela
        </p>
        <div ref={ref} style={{ display: "flex", justifyContent: "center", marginBottom: 16, minHeight: 65 }} />
        <button
          className="btn primary"
          style={{ width: "100%" }}
          disabled={!ready}
          title={ready ? undefined : "Najpierw potwierdź, że nie jesteś botem"}
          onClick={() => instance.loginPopup(loginRequest)}
        >
          Zaloguj się kontem Microsoft
        </button>
        <p className="faint" style={{ marginTop: 18, fontSize: 11.5 }}>
          {verified
            ? "Dostęp wyłącznie dla właściciela. Inne konta zostaną odrzucone przez API."
            : ready
              ? "Weryfikacja pominięta (Turnstile niedostępny) — dostęp i tak tylko dla właściciela (logowanie Microsoft)."
              : "Weryfikacja antybotowa — poczekaj chwilę lub potwierdź powyżej, aby odblokować logowanie."}
        </p>
      </div>
    </div>
  );
}

/** Shown when authenticated but the backend rejects the owner gate (403). */
export function AccessDenied({ onRetry }: { onRetry: () => void }) {
  const { instance } = useMsal();
  return (
    <div className="login-screen">
      <div className="card login-card fade-up">
        <div className="brand-mark" style={{ background: "var(--critical)" }}><IconShield size={24} /></div>
        <h2 style={{ fontSize: 18 }}>Brak dostępu</h2>
        <p className="muted" style={{ marginTop: 6, marginBottom: 24, fontSize: 13 }}>
          To konto nie jest właścicielem. Konsola jest dostępna tylko dla wskazanego konta/grupy.
        </p>
        <div className="row" style={{ justifyContent: "center" }}>
          <button className="btn" onClick={onRetry}>Spróbuj ponownie</button>
          <button className="btn ghost" onClick={() => instance.logoutPopup()}>Wyloguj</button>
        </div>
      </div>
    </div>
  );
}
